import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

import { useGetWorkerShifts } from "../../WorkerShifts/api/useGetWorkerShifts";
import { hasPreShiftOrientation } from "./hasPreShiftOrientation";
import { useWorkplacesWithOrientationCredentials } from "./useWorkplacesWithOrientationCredentials";
import { useWorkplacesWithUpcomingPreShiftOrientation } from "./useWorkplacesWithUpcomingPreShiftOrientation";

interface UseEvaluatePreShiftOrientationForOpenShiftParams {
  shiftStartIso: string;
  workplaceId: string;
  preShiftOrientationLengthInMinutes: number | undefined;
}

interface UseEvaluatePreShiftOrientationForOpenShiftResult {
  hasPreShiftOrientation: boolean;
  adjustedStart?: Date;
  orientationLengthInMinutes?: number;
  isLoading: boolean;
}

export function useEvaluatePreShiftOrientationForOpenShift(
  params: UseEvaluatePreShiftOrientationForOpenShiftParams
): UseEvaluatePreShiftOrientationForOpenShiftResult {
  const { shiftStartIso, workplaceId, preShiftOrientationLengthInMinutes } = params;

  const worker = useDefinedWorker();

  const { workplacesWithOrientationCredentials, isLoading: isLoadingCredentials } =
    useWorkplacesWithOrientationCredentials();
  const { workplacesWithUpcomingPreShiftOrientation, isLoading: isLoadingActiveOrientations } =
    useWorkplacesWithUpcomingPreShiftOrientation();
  const { shifts: workerShifts, isLoading: isLoadingBookedShifts } = useGetWorkerShifts({ worker });

  const bookedShifts = workerShifts.map((workerShift) => ({
    start: workerShift.start,
    workplaceId: workerShift.facility.userId ?? "",
  }));

  const isLoading = isLoadingCredentials || isLoadingActiveOrientations || isLoadingBookedShifts;

  if (isLoading) {
    return {
      hasPreShiftOrientation: false,
      isLoading,
    };
  }

  const evaluation = hasPreShiftOrientation({
    workplaceId,
    shiftStart: shiftStartIso,
    preShiftOrientationLengthInMinutes,
    workplacesWithOrientationCredentials,
    workplacesWithUpcomingPreShiftOrientation,
    bookedShifts,
  });

  return {
    ...evaluation,
    isLoading,
  };
}
